import * as React from "react";
import {StorageSuccessModal} from "./StorageSuccessModal";
import {ValuableItem, Party, Types, ICustomerPortalService, Policy, ProductsEnum} from "@falcon/services";
import {lazyInject} from "@athena-portal/core";
import {getAmountWithDecimalDigits, getPolicyInteraction} from "../../CommonFunctions";
import {
    EditCarMtaSuccessModalWordingGenerator
} from "../../pages/dashboard/annual/motor/yourCarsPage/modal/EditCarMtaSuccessModalWordingGenerator";

interface IProps {
    policies?: Policy[];
}

interface IState {
    party?: Party;
    policies?: Policy[];
}

export class ValuablesSuccessModal extends React.Component<IProps, IState> {
    private showSuccessPopup: string | null = window.sessionStorage.getItem("showPopup");
    private referrerJourney: string | null = window.sessionStorage.getItem("referrerJourney");
    private title: string;
    private modalContent: JSX.Element;

    @lazyInject(Types.CustomerPortalService)
    private customerPortalService: ICustomerPortalService;

    public constructor(props: IProps) {
        super(props);
        this.state = {};
    }

    public componentDidMount(): void {
        this.customerPortalService.getProfile().subscribe((party: Party) => this.setState({party}));
        this.customerPortalService.getPolicies(ProductsEnum.RENTERS).subscribe((policies: Policy[]) => this.setState({policies}));
    }

    public render(): React.ReactNode {
        if (this.showSuccessPopup && this.state.party && this.state.policies) {
            return <>{this.prepareModal() && <StorageSuccessModal title={this.title} explanationWording={this.modalContent} anythingElseTitle={EditCarMtaSuccessModalWordingGenerator.generateAnythingElseChangedModalTitle()} explanationAnythingElseWording={EditCarMtaSuccessModalWordingGenerator.generateAnythingElseChangedModalBody()}/>}</>
        } else {
            return <></>
        }
    }

    private prepareModal = (): boolean => {
        if (this.referrerJourney === "mtaAddItem") {
            this.setMtaAddItem();
        } else if (this.referrerJourney === "mtaEditItem") {
            this.setMtaEditItem();
        } else if (this.referrerJourney === "mtaRemoveItem") {
            this.setMtaRemoveItem();
        } else if (this.referrerJourney === "mtaEmploymentStatus") {
            this.setMtaEmploymentStatus();
        } else if (this.referrerJourney === "mtaOtherResidents") {
            this.setMtaOtherResidents();
        } else if (this.referrerJourney === "mtaEditRentersCover") {
            this.setMtaEditRentersCover();
        } else if (this.referrerJourney === "mtaEditRentersClaims") {
            this.setMtaEditRentersClaims();
        } else if (this.referrerJourney === "mtaReinstateRentersCover")  {
            this.setReactivateRentersCoverPopup();
        } else {
            // clear Session Storage and prevent showing pop-up without data if referrerJourney not set or set wrongly
            this.showSuccessPopup = null;
            window.sessionStorage.removeItem("sessionInteraction");
            window.sessionStorage.removeItem("showPopup");
            window.sessionStorage.removeItem("referrerJourney");
            return false;
        }
        return true;
    };

    private setMtaAddItem = (): void => {
        const interaction: any = getPolicyInteraction(window.sessionStorage.getItem("sessionInteraction"), this.state.policies);
        const additionalPremium: string = interaction.factors.additionalPremium;
        const mtaAddItemDescriptions: string[] = JSON.parse(window.sessionStorage.getItem("mtaAddItemDescriptions")!);

        this.title = "Payment was successful";
        this.modalContent =
            <p>
                <b>{getAmountWithDecimalDigits(additionalPremium)}</b> have been debited from your card <b>for adding cover for</b>
                {...mtaAddItemDescriptions.map((description: string, index: number) => {
                    if (index === mtaAddItemDescriptions.length - 1) {
                        return <span key={index}><b> {description}</b></span>
                    }
                    return <span key={index}><b> {description}</b> and</span>
                })}
            </p>
    };

    private setMtaEditItem = (): void => {
        const interaction: any = getPolicyInteraction(window.sessionStorage.getItem("sessionInteraction"), this.state.policies);
        const additionalPremium: string = interaction.factors.additionalPremium;
        const returnPremium: string = interaction.factors.returnPremium;
        const itemToProcess: ValuableItem = JSON.parse(window.sessionStorage.getItem("itemToProcess")!);

        if (additionalPremium) {
            this.title = "Payment was successful";
            this.modalContent =
                <p>
                    <b>{getAmountWithDecimalDigits(additionalPremium)}</b> have been debited from your card <b>for changing cover value of the {itemToProcess.description} to {itemToProcess.value}</b>
                </p>
        }

        if (returnPremium) {
            this.title = "Your refund is on the way";
            this.modalContent =
                <p>
                    We’ve saved your changes, and we’ll refund you <b>{getAmountWithDecimalDigits(returnPremium)} </b>
                    for removing the cover for your <b>{itemToProcess.description}. </b>
                    Please allow two days for it to arrive.
                </p>
        }
    };

    private setMtaRemoveItem = (): void => {
        const interaction: any = getPolicyInteraction(window.sessionStorage.getItem("sessionInteraction"), this.state.policies);
        const returnPremium: string = interaction.factors.returnPremium;
        const refund: boolean = interaction.factors.returnPremium > 0;
        const itemToProcess: ValuableItem = JSON.parse(window.sessionStorage.getItem("itemToProcess")!);
        if (refund) {
            this.title = "Your refund is on the way";
            this.modalContent =
                <p>
                    We’ve saved your changes, and we’ll refund you <b>{getAmountWithDecimalDigits(returnPremium)} </b>
                    for removing the cover for your <b>{itemToProcess.description}. </b>
                    Please allow two days for it to arrive.
                </p>
        } else {
            this.title = "All done";
            this.modalContent =
                <p>
                    We’ve saved your changes, and there is no refund for removing your <b>{itemToProcess.description}.</b>
                </p>
        }
    };

    private setMtaEmploymentStatus = (): void => {
        const mtaEmploymentStatusAdditionalPremium: string | null = window.sessionStorage.getItem("mtaEmploymentStatusAdditionalPremium");
        const mtaEmploymentStatusReturnPremium: string | null = window.sessionStorage.getItem("mtaEmploymentStatusReturnPremium");
        const mtaEmploymentStatuses: { previousStatus: string, newStatus: string } = JSON.parse(window.sessionStorage.getItem("mtaEmploymentStatuses")!);

        if (mtaEmploymentStatusAdditionalPremium) {
            this.title = "Payment was successful";
            this.modalContent =
                <p>
                    <b>{getAmountWithDecimalDigits(mtaEmploymentStatusAdditionalPremium)} </b>
                    have been debited from your card
                    <b> for changing account holder's employment status from {mtaEmploymentStatuses.previousStatus} to {mtaEmploymentStatuses.newStatus}</b>
                </p>
        } else if (mtaEmploymentStatusReturnPremium) {
            this.title = "Your refund is on the way";
            this.modalContent =
                <p>
                    We’ve saved your changes, and we’ll refund you <b>{getAmountWithDecimalDigits(mtaEmploymentStatusReturnPremium)}</b>
                    <b> for changing account holder's employment status from {mtaEmploymentStatuses.previousStatus} to {mtaEmploymentStatuses.newStatus}. </b>
                    Please allow two days for it to arrive.
                </p>
        } else {
            this.title = "All done";
            this.modalContent =
                <p>
                    We’ve saved your changes, and there’s no extra cost
                    <b> for changing account holder's employment status from {mtaEmploymentStatuses.previousStatus} to {mtaEmploymentStatuses.newStatus}</b>
                </p>

        }
    };

    private setMtaOtherResidents = (): void => {
        const interaction: any = getPolicyInteraction(window.sessionStorage.getItem("sessionInteraction"), this.state.policies);
        const additionalPremium: string = interaction.factors.additionalPremium;
        const returnPremium: string = interaction.factors.returnPremium;

        if (additionalPremium) {
            this.title = "Payment was successful";
            this.modalContent =
                <p>
                    <b>{getAmountWithDecimalDigits(additionalPremium)} </b>
                    have been debited from your card
                    <b> for editing the number of other residents who live at the property.</b>
                </p>
        } else if (returnPremium) {
            this.title = "Your refund is on the way";
            this.modalContent =
                <p>
                    We’ve saved your changes, and we’ll refund you <b>{getAmountWithDecimalDigits(returnPremium)}</b>
                    <b> for editing the number of other residents who live at the property. </b>
                    Please allow two days for it to arrive.
                </p>
        } else {
            this.title = "All done";
            this.modalContent =
                <p>
                    We’ve saved your changes, and there’s no extra cost
                    <b> for editing the number of other residents who live at the property.</b>
                </p>

        }
    };

    private setMtaEditRentersCover = (): void => {
        const interaction: any = getPolicyInteraction(window.sessionStorage.getItem("sessionInteraction"), this.state.policies);
        const additionalPremium: string = interaction.factors.additionalPremium;
        const returnPremium: string = interaction.factors.returnPremium;
        const mtaEditRentersCoverChanges: string = window.sessionStorage.getItem("mtaEditRentersCoverChanges")!;

        if (additionalPremium) {
            this.title = "Payment was successful";
            this.modalContent =
                <p>
                    <b>{getAmountWithDecimalDigits(additionalPremium)} </b>
                    have been debited from your card
                    <b> for changing {mtaEditRentersCoverChanges}.</b>
                </p>
        } else if (returnPremium) {
            this.title = "Your refund is on the way";
            this.modalContent =
                <p>
                    We’ve saved your changes, and we’ll refund you <b>{getAmountWithDecimalDigits(returnPremium)}</b>
                    <b> for changing {mtaEditRentersCoverChanges}. </b>
                    Please allow two days for it to arrive.
                </p>
        } else {
            this.title = "All done";
            this.modalContent =
                <p>
                    We’ve saved your changes, and there’s no extra cost
                    <b> for changing {mtaEditRentersCoverChanges}.</b>
                </p>
        }
    };

    private setMtaEditRentersClaims = (): void => {
        const interaction: any = getPolicyInteraction(window.sessionStorage.getItem("sessionInteraction"), this.state.policies);
        const additionalPremium: string = interaction.factors.additionalPremium;
        const returnPremium: string = interaction.factors.returnPremium;
        const mtaEditRentersClaimsChanges: string = window.sessionStorage.getItem("mtaEditRentersClaimsChanges")!;

        if (additionalPremium) {
            this.title = "Payment was successful";
            this.modalContent =
                <p>
                    <b>{getAmountWithDecimalDigits(additionalPremium)} </b>
                    have been debited from your card
                    <b> for changing {mtaEditRentersClaimsChanges}.</b>
                </p>
        } else if (returnPremium) {
            this.title = "Your refund is on the way";
            this.modalContent =
                <p>
                    We’ve saved your changes, and we’ll refund you <b>{getAmountWithDecimalDigits(returnPremium)}</b>
                    <b> for changing {mtaEditRentersClaimsChanges}. </b>
                    Please allow two days for it to arrive.
                </p>
        } else {
            this.title = "All done";
            this.modalContent =
                <p>
                    We’ve saved your changes, and there’s no extra cost
                    <b> for changing {mtaEditRentersClaimsChanges}.</b>
                </p>
        }
    };

    private setReactivateRentersCoverPopup = (): void => {

        if (this.props.policies) {
            const interaction: any = getPolicyInteraction(window.sessionStorage.getItem("sessionInteraction"), this.props.policies);

            if (interaction.factors.additionalPremium && interaction.factors.additionalPremium > 0) {
                this.title = "Payment successful";
                this.modalContent =
                    <p>
                        We’ve saved your changes, and <b>{getAmountWithDecimalDigits(interaction.factors.additionalPremium)}</b> has been debited from your card {' '}
                        for reactivating your renters cover
                    </p>
            } else {
                this.title = "All done";
                this.modalContent =
                    <p>
                        We’ve saved your changes, and there’s no extra cost for reactivating your renters cover
                    </p>
            }
        }
    };
}



// WEBPACK FOOTER //
// ./src/components/modals/ValuablesSuccessModal.tsx